<template>
  <v-data-table
    :headers="_headers"
    :items="invoices"
    fixed-header
    :height="!Boolean(clientId) ? '70vh' : ''"
    :items-per-page="-1"
    hide-default-footer
    :dense="Boolean(clientId)"
    :key="invoices.length"
    item-key="invoicePaymentId"
  >
    <template v-slot:[`item.paidAmount`]="{ item }">
      <v-edit-dialog v-if="!noEdit" large @save="updatePayment(item)">
        {{ item.formattedPaidAmount }}
        <template v-slot:input>
          <validation-provider
            :rules="validations.paidAmount"
            v-slot="{ errors }"
          >
            <v-text-field
              v-model="item.newPaidAmount"
              @change="onPaidAmountChange(item, $event)"
              type="number"
              dense
              prepend-icon="mdi-currency-usd"
              single-line
              autofocus
              :error-messages="errors"
            >
            </v-text-field>
          </validation-provider>
        </template>
      </v-edit-dialog>
      <span v-else>{{ item.formattedPaidAmount }}</span>
    </template>

    <template v-slot:[`item.adjustmentAmount`]="{ item }">
      <v-edit-dialog v-if="!noEdit" large @save="updatePayment(item)">
        {{ item.formattedAdjustmentAmount }}
        <template v-slot:input>
          <validation-provider :rules="{ required: true }" v-slot="{ errors }">
            <v-text-field
              v-model="item.newAdjustmentAmount"
              type="number"
              dense
              prepend-icon="mdi-currency-usd"
              single-line
              autofocus
              :error-messages="errors"
            >
            </v-text-field>
          </validation-provider>
        </template>
      </v-edit-dialog>
      <span v-else>{{ item.formattedAdjustmentAmount }}</span>
    </template>

    <template v-slot:[`item.adjustmentType`]="{ item }">
      <v-edit-dialog v-if="!noEdit" large @save="updatePayment(item)">
        {{ item.adjustmentType || "N/A" }}
        <template v-slot:input>
          <v-select
            v-model="item.newAdjustmentType"
            :items="adjustmentTypes"
            dense
            autofocus
            clearable
          ></v-select>
        </template>
      </v-edit-dialog>
      <span v-else> {{ item.adjustmentType || "N/A" }}</span>
    </template>

    <template v-slot:[`item.actions`]="{ item }">
      <v-tooltip left :disabled="!invoiceIncludedInBatchInPayment(item)">
        <template v-slot:activator="{ on }">
          <div v-on="on">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  color="primary"
                  v-on="on"
                  icon
                  :disabled="invoiceIncludedInBatchInPayment(item)"
                  @click="removeInvoice(item)"
                >
                  <v-icon small>mdi-minus-circle-outline</v-icon>
                </v-btn>
              </template>
              <span>Remove Invoice</span>
            </v-tooltip>
          </div>
        </template>
        <span
          >Session is included in an Invoice Batch that is part of this
          payment.</span
        >
      </v-tooltip>
    </template>
  </v-data-table>
</template>
<script>
import { required, minValue } from "../../plugins/vee-validate/validation";
export default {
  props: ["noEdit", "clientId"],
  data() {
    return {
      headers: [
        { text: "Client", value: "clientName", hide: Boolean(this.clientId) },
        { text: "OSIS", value: "clientOsis", hide: Boolean(this.clientId) },
        { text: "Date of Service", value: "formattedDos" },
        { text: "Service", value: "serviceName", hide: Boolean(this.clientId) },
        {
          text: "Invoice Date",
          value: "formattedInvoiceDate",
          hide: Boolean(this.clientId),
        },
        {
          text: "Billed",
          value: "formattedBilledAmount",
          sortable: false,
        },
        {
          text: "Balance",
          value: "formattedBalance",
          sortable: false,
          hide: this.noEdit,
        },
        {
          text: "Payment",
          value: "paidAmount",
          sortable: false,
          cellClass: "font-weight-medium",
        },
        {
          text: "Adjustment",
          value: "adjustmentAmount",
          sortable: false,
          cellClass: "font-weight-medium",
        },
        {
          text: "Adjustment Type",
          value: "adjustmentType",
          sortable: false,
          cellClass: "font-weight-medium",
        },
        {
          text: "New Balance",
          value: "formattedNewBalance",
          sortable: false,
          hide: this.noEdit,
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          hide: this.noEdit,
        },
      ],
    };
  },
  mounted() {
    // set default state for edit-dialogs
    this.reset();
  },
  computed: {
    _headers() {
      return this.headers.filter((x) => !x.hide);
    },
    batch() {
      return this.$store.state.paymentBatchModule.selectedBatch;
    },
    invoices() {
      if (Boolean(this.clientId)) {
        return this.batch?.invoicePayments.filter(
          (x) => x.clientId == this.clientId
        );
      }
      return this.batch?.invoicePayments;
    },
    validations() {
      return {
        paidAmount: {
          required: true,
          minValue: 0,
        },
      };
    },
    adjustmentTypes() {
      return this.$store.state.optionsModule.adjustmentTypes;
    },
  },
  methods: {
    reset() {
      this.invoices.forEach((item, index) => {
        this.invoices[index].newPaidAmount = item.paidAmount;
        this.invoices[index].newAdjustmentAmount = item.adjustmentAmount;
        this.invoices[index].newAdjustmentType = item.adjustmentType;
      });
    },
    onPaidAmountChange(item, $event) {
      // automatically set adjustment
      const index = this.invoices.findIndex(
        (t) => t.invoicePaymentId == item.invoicePaymentId
      );
      this.invoices[index].newAdjustmentAmount = item.balance - $event;
    },
    async updatePayment(item) {
      this.$store.commit("uxModule/setShowLoader", true);
      let success = await this.$store.dispatch(
        "paymentBatchModule/updateInvoiceInBatch",
        {
          invoicePaymentId: item.invoicePaymentId,
          paymentBatchId: item.paymentBatchId,
          sessionInvoiceId: item.sessionInvoiceId,
          ...(item.newPaidAmount != null
            ? { paidAmount: item.newPaidAmount }
            : { paidAmount: item.paidAmount }),
          ...(item.newAdjustmentAmount != null
            ? { adjustmentAmount: item.newAdjustmentAmount }
            : { adjustmentAmount: item.adjustmentAmount }),
          adjustmentType: item.newAdjustmentType,
        }
      );
      this.$store.commit("uxModule/setShowLoader", false);
      if (!success) {
        this.$store.commit(
          "uxModule/setSnackbarMsg",
          "Failed to update payment"
        );
        this.$store.commit("uxModule/setShowSnackbar", true);
      }
      this.reset();
    },
    invoiceIncludedInBatchInPayment(item) {
      return (
        // session included in an invoice batch that is part of this payment
        item.invoiceBatchId != null &&
        this.batch.invoiceBatches?.length > 0 &&
        this.batch.invoiceBatches
          .map((x) => x.invoiceBatchId)
          .includes(item.invoiceBatchId)
      );
    },
    async removeInvoice(item) {
      this.$store.commit("uxModule/setShowLoader", true);
      await this.$store.dispatch("paymentBatchModule/deleteInvoiceFromBatch", {
        invoicePaymentId: item.invoicePaymentId,
      });
      this.$store.commit("uxModule/setShowLoader", false);
    },
  },
};
</script>
